import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import dayjs from 'dayjs';
import Icon from 'chameleon/ui-stack/ui/base/icon';
import ManualTimeSelection from '../ManualTimeSelection';
import DATA_TEST_IDS from 'chameleon-src/components/assign/AssignManageForm/utils/dataTestIds';

/**
 * Custom input for date picker.
 * */
const DatePickerInput = (props) => {
  const {
    customClass,
    value,
    placeholder,
    onClick,
    onReset,
    onManualTimeSet,
    hasResetButton,
  } = props;
  const [yearDayMonthLabel, setYearDayMonthLabel] = useState('');
  const [hoursMinutesLabel, setHoursMinutesLabel] = useState('');

  useEffect(() => {
    if (value.length) {
      const date = dayjs(value);
      setYearDayMonthLabel(date.format('MM/DD/YYYY'));
      setHoursMinutesLabel(date.format('hh:mm A'));
    }
  }, [value]);

  /**
   * Helper for getting the content to display.
   * @returns {object} - The content to display (value or placeholder).
   * */
  const getContent = () => {
    if (value.length) {
      return (
        <Fragment>
          <span data-test-id={DATA_TEST_IDS.DATE_YEARS_MONTH_LABEL}>
            {yearDayMonthLabel}
          </span>
          <ManualTimeSelection
            value={hoursMinutesLabel}
            setManualTime={onManualTimeSet}
          />
        </Fragment>
      );
    }

    if (placeholder) {
      return <span>{placeholder}</span>;
    }

    return defaultPlaceholder;
  };

  const handleReset = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onReset();
  };

  /**
   * Default content if the value isn't selected and there isn't a custom placeholder defined.
   * */
  const defaultPlaceholder = (
    <Fragment>
      <span>MM/DD/YYYY</span>
      <span>HH:MM</span>
    </Fragment>
  );

  return (
    <div
      role="button"
      tabIndex="0"
      className={cn('datepicker__input--custom', customClass)}
      onClick={onClick}
    >
      <Icon id="calendar" />
      {getContent()}
      {onReset && hasResetButton && (
        <div
          data-test-id={DATA_TEST_IDS.DATEPICKER_RESET_BUTTON}
          role="button"
          tabIndex="0"
          className="reset-container"
          onClick={handleReset}
        >
          <Icon id="remove_circle" width="14" height="14" />
        </div>
      )}
    </div>
  );
};

DatePickerInput.displayName = 'DatePickerInput';

DatePickerInput.propTypes = {
  // The placeholder to display when no date is selected.
  placeholder: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  // The callback for clicking the input.
  onClick: PropTypes.func,
  // The value to display.
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  // Custom class to style the element.
  customClass: PropTypes.string,
  onReset: PropTypes.func,
  hasResetButton: PropTypes.bool,
};

DatePickerInput.defaultProps = {
  placeholder: null,
  onClick: null,
  value: null,
  customClass: null,
  onReset: null,
  hasResetButton: true,
};

export default DatePickerInput;
